import {
  BUILD_TYPE_META,
  type BuildType,
  type LeadRecord,
} from '@flashpoint/site-dna';

export type WizardStepId =
  | 'engagement'
  | 'business'
  | 'domain'
  | 'migration'
  | 'content'
  | 'context'
  | 'vibe'
  | 'layout'
  | 'palette'
  | 'assets'
  | 'sections'
  | 'review';

export interface WizardStepDescriptor {
  id: WizardStepId;
  title: string;
  /** Mobile-friendly subtitle shown under the step number. */
  subtitle: string;
  /** Should this step be skipped given the current lead data? */
  skipIf?: (lead: LeadRecord) => boolean;
}

export const STEP_LIST: ReadonlyArray<WizardStepDescriptor> = [
  { id: 'engagement', title: 'Engagement', subtitle: 'Build type + how this is paid' },
  { id: 'business', title: 'Business', subtitle: 'Who they are' },
  { id: 'domain', title: 'Domain', subtitle: 'Where it will live' },
  {
    id: 'migration',
    title: 'Migration',
    subtitle: 'Coming from another platform',
    skipIf: (lead) => !BUILD_TYPE_META[lead.engagement.buildType].requiresMigration,
  },
  { id: 'content', title: 'Content readiness', subtitle: 'Copy, photos, logo status' },
  { id: 'context', title: 'Context', subtitle: 'Integrations, compliance, language' },
  { id: 'vibe', title: 'Vibe', subtitle: '9 color/typography flavors of the Nuzum Method' },
  { id: 'layout', title: 'Layout shell', subtitle: 'Auto, or pick 1 of 12 compositions' },
  { id: 'palette', title: 'Multi-tone palette', subtitle: 'No pure white or black' },
  { id: 'assets', title: 'Assets', subtitle: 'Logo, hero, gallery uploads' },
  { id: 'sections', title: 'Sections', subtitle: 'Order them top to bottom' },
  { id: 'review', title: 'Review', subtitle: 'Final check + Run' },
];

export function activeSteps(lead: LeadRecord): WizardStepDescriptor[] {
  return STEP_LIST.filter((s) => !s.skipIf || !s.skipIf(lead));
}

export function indexOfStep(steps: WizardStepDescriptor[], id: WizardStepId): number {
  return steps.findIndex((s) => s.id === id);
}

export function nextStep(
  steps: WizardStepDescriptor[],
  current: WizardStepId,
): WizardStepId | null {
  const i = indexOfStep(steps, current);
  if (i === -1 || i === steps.length - 1) return null;
  return steps[i + 1]!.id;
}

export function prevStep(
  steps: WizardStepDescriptor[],
  current: WizardStepId,
): WizardStepId | null {
  const i = indexOfStep(steps, current);
  if (i <= 0) return null;
  return steps[i - 1]!.id;
}

export function defaultStep(lead: LeadRecord): WizardStepId {
  const stored = lead.wizardCursor as WizardStepId | undefined;
  const list = activeSteps(lead);
  if (stored && list.some((s) => s.id === stored)) return stored;
  return list[0]!.id;
}

/**
 * Industry verticals for the Call Sheet picker and wizard suggestions.
 * Freeform input is still allowed.
 */
export const INDUSTRY_GROUPS: ReadonlyArray<{ label: string; items: readonly string[] }> = [
  {
    label: 'Trades',
    items: ['Plumbing', 'HVAC', 'Electrical', 'Roofing', 'Landscaping', 'Cleaning', 'Construction', 'Automotive'],
  },
  {
    label: 'Health and personal',
    items: ['Dental', 'Medical', 'Chiropractic', 'Veterinary', 'Salon', 'Barber', 'Fitness', 'Yoga'],
  },
  {
    label: 'Food',
    items: ['Restaurant', 'Cafe', 'Bakery'],
  },
  {
    label: 'Professional',
    items: [
      'Real Estate',
      'Consulting',
      'Marketing Agency',
      'SaaS',
      'E-commerce',
      'Tutoring',
      'Childcare',
      'Pet Services',
    ],
  },
  {
    label: 'Legal and finance',
    items: ['Legal', 'Accounting', 'Insurance'],
  },
  {
    label: 'Creative',
    items: ['Photography', 'Wedding'],
  },
];

export const INDUSTRY_SUGGESTIONS: ReadonlyArray<string> = INDUSTRY_GROUPS.flatMap((g) => [...g.items]);

export function defaultBuildTypeForCreation(): BuildType {
  return 'Fresh Build';
}
